import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lobbyDayApi } from '../lib/api'
import { useJobPolling, type UseJobPollingOptions } from './useJobPolling'
import type { SaveVotesRequest, RollCallFillResult } from '../types/lobbyDay'

export function useReconciliation(eventId: string | undefined) {
  return useQuery({
    queryKey: ['lobby-day', 'reconciliation', eventId],
    queryFn: async () => (await lobbyDayApi.getReconciliation(eventId!)).data,
    enabled: !!eventId,
  })
}

// Wave 3 (C.3): saving votes now applies sentiment tags in the same request (see
// LobbyReconciliationController.SaveVotes), so this mutation carries the invalidations that used
// to live on useApplyReconciliation's onSuccess -- including the attendees invalidation, which is
// easy to drop silently since nothing else here reads it.
export function useSaveReconciliationVotes(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: SaveVotesRequest) =>
      (await lobbyDayApi.saveReconciliationVotes(eventId, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
      // sentiment tags now live on attendees → refresh the Attendees cockpit tag chips
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] })
    },
  })
}

// Endpoint retained as the idempotent recovery path (POST .../apply) -- kept exported even though
// ReconciliationTab no longer calls it directly now that saving votes auto-applies.
export function useApplyReconciliation(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await lobbyDayApi.applyReconciliation(eventId)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
      // sentiment tags now live on attendees → refresh the Attendees cockpit tag chips
      qc.invalidateQueries({ queryKey: ['lobby-day', 'attendees', eventId] })
    },
  })
}

// ---------- Roll-call auto-fill (LegiScan Slice 2) ----------

/**
 * Enqueues a LegiScan roll-call auto-fill for one ask's linked bills (job tier, NEW-07), then
 * polls the job to completion. Mirrors useSyncLegislators + useJobPolling's split, per
 * frontend/src/CLAUDE.md's job-polling rule: the enqueue mutation itself does NOT invalidate --
 * only the poll's terminal Succeeded branch does, and it also surfaces the RollCallFillResult
 * payload so a consuming component doesn't have to reach into job.result itself.
 */
export function useFillVotes(eventId: string, askId: string, options?: UseJobPollingOptions) {
  const qc = useQueryClient()
  const [jobId, setJobId] = useState<string | null>(null)
  const [result, setResult] = useState<RollCallFillResult | null>(null)

  const enqueue = useMutation({
    mutationFn: async () => (await lobbyDayApi.fillVotes(eventId, askId)).data,
  })

  const poll = useJobPolling(jobId, options)

  useEffect(() => {
    if (!jobId || !poll.isTerminal) return

    if (poll.data?.status === 'Succeeded') {
      setResult((poll.data.result as RollCallFillResult | undefined) ?? null)
      qc.invalidateQueries({ queryKey: ['lobby-day', 'reconciliation', eventId] })
    }
    setJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, poll.isTerminal, poll.data])

  const fill = async () => {
    const { jobId: newJobId } = await enqueue.mutateAsync()
    setJobId(newJobId)
    return newJobId
  }

  return {
    fill,
    result,
    isPending: enqueue.isPending || (!!jobId && !poll.isTerminal),
    isError: enqueue.isError || poll.isError,
    poll,
  }
}
